import { Injectable } from "@angular/core"
import { Http } from "@angular/http"
import { cloneDeep, extend, get } from "lodash"

import { Merchant } from "../shared/merchant"
import {
  MerchantReferralCampaign,
  MerchantReferralCampaignPagesTemplate,
  MerchantReferralInvite
} from "../shared/merchant-referral"
import { ToolService } from "../tool.service"
import { AdvocateInfo } from "./advocate-info"
import { AdvocateReferralForm } from "./advocate-referral-form"
import { Referral } from "./referral"

@Injectable()
export class ReferralService {
  is_processing = false

  private advocate_info_cache: { [s: string]: AdvocateInfo } = {}
  private merchant_cache: { [s: string]: Merchant } = {}

  constructor(
    private http: Http,
    private toolService: ToolService
  ) {}

  getMerchant(merchant_slug: string): Promise<Merchant> {
    return new Promise((resolve, reject) => {
      if (this.merchant_cache[merchant_slug]) {
        return resolve(this.merchant_cache[merchant_slug])
      }

      this.http.get(`/merchants/${merchant_slug}?embed=rewards`, this.toolService.getApiOptions()).subscribe(
        (response) => {
          const merchant = response.json() as Merchant
          this.merchant_cache[merchant_slug] = merchant
          return resolve(merchant)
        },
        (error) => {
          return reject(this.toolService.parseApiError(error))
        }
      )
    })
  }

  getMerchantReferralCampaign(merchant_id: string): Promise<MerchantReferralCampaign> {
    return new Promise((resolve, reject) => {
      this.http.get(`/merchants/${merchant_id}/merchant_campaign/current`, this.toolService.getApiOptions()).subscribe(
        (response) => resolve(response.json() as MerchantReferralCampaign),
        (error) => reject(this.toolService.parseApiError(error))
      )
    })
  }

  getMerchantTerms(merchant_slug: string): Promise<string> {
    return this.getMerchant(merchant_slug).then((referral_merchant) => {
      return referral_merchant.settings.referral_magic
        ? referral_merchant.settings.referral_magic.terms
        : "Referral program has been disabled"
    })
  }

  createAdvocate(merchant_slug: string, advocate_form: AdvocateReferralForm): Promise<AdvocateInfo> {
    return new Promise((resolve, reject) => {
      this.http.post(`/merchants/${merchant_slug}/advocate`, advocate_form, this.toolService.getApiOptions()).subscribe(
        (response) => {
          const advocate_info = response.json() as AdvocateInfo
          this.advocate_info_cache[advocate_info.referrer_code] = advocate_info
          return resolve(advocate_info)
        },
        (error) => reject(this.toolService.parseApiError(error))
      )
    })
  }

  getAdvocateInfo(merchant_slug: string, advocate_token: string): Promise<AdvocateInfo> {
    return new Promise((resolve, reject) => {
      if (this.advocate_info_cache[advocate_token]) {
        return resolve(this.advocate_info_cache[advocate_token])
      }
      this.http
        .get(`/merchants/${merchant_slug}/advocate/${advocate_token}`, this.toolService.getApiOptions())
        .subscribe(
          (response) => resolve(response.json() as AdvocateInfo),
          (error) => reject(this.toolService.parseApiError(error))
        )
    })
  }

  createAdvocateReferral(
    merchant_slug: string,
    advocate_token: string,
    customer_info: AdvocateReferralForm,
    is_invite?: boolean
  ): Promise<any> {
    let url = `/merchants/${merchant_slug}/advocate/${advocate_token}/referral`

    if (is_invite === true) {
      url += `?is_invite=true`
    }

    return new Promise((resolve, reject) => {
      this.http.post(url, customer_info, this.toolService.getApiOptions()).subscribe(
        (response) => {
          if (response) {
            return resolve(response.json() as Referral)
          }

          return resolve()
        },
        (error) => reject(this.toolService.parseApiError(error))
      )
    })
  }

  getAdvocateInvite(merchant_id: string, invite_id: string): Promise<MerchantReferralInvite> {
    return new Promise((resolve, reject) => {
      this.http
        .get(`/merchants/${merchant_id}/referral_invites/${invite_id}?embed=advocate`, this.toolService.getApiOptions())
        .subscribe(
          (response) => resolve(response.json() as MerchantReferralInvite),
          (error) => reject(this.toolService.parseApiError(error))
        )
    })
  }

  getFriendInvite(merchant_id: string, advocate_token: string, invite_id: string): Promise<MerchantReferralInvite> {
    return new Promise((resolve, reject) => {
      this.http
        .get(
          `/merchants/${merchant_id}/advocate/${advocate_token}/invites/${invite_id}`,
          this.toolService.getApiOptions()
        )
        .subscribe(
          (response) => resolve(response.json() as MerchantReferralInvite),
          (error) => reject(this.toolService.parseApiError(error))
        )
    })
  }

  sendInvite(merchant_id: string, advocate_token: string, customer_info: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http
        .post(
          `/merchants/${merchant_id}/advocate/${advocate_token}/invites`,
          customer_info,
          this.toolService.getApiOptions()
        )
        .subscribe(
          (response) => resolve(),
          (error) => reject(this.toolService.parseApiError(error))
        )
    })
  }

  getAdvocateSummary(merchant_slug: string, advocate_token: string, token: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http
        .get(`/merchants/${merchant_slug}/advocate/${advocate_token}/summary`, this.toolService.getApiOptions())
        .subscribe(
          (response) => {
            const summary: any = response.json()
            return resolve(summary)
          },
          (error) => reject(this.toolService.parseApiError(error))
        )
    })
  }

  replaceSharingDynamicValues(text: string, merchant: Merchant, advocate_info: AdvocateInfo, sharing_url?: string) {
    text = this.replaceDynamicValues(text, merchant, advocate_info)

    if (sharing_url) {
      if (text.indexOf("[Sharing Link]") !== -1) {
        text = this.toolService.replaceAll(text, "[Sharing Link]", `${sharing_url}`)
      } else {
        text += `\n\r${sharing_url}`
      }
    }
    return text
  }

  getSharingUrl(merchant: Merchant, advocate_info: AdvocateInfo, ref?: string): string {
    const sharing_url = `${window.location.protocol}//${window.location.host}/${merchant.slug}/${advocate_info.referrer_token}`
    return `${sharing_url}?ref=${ref}`
  }

  openSharingSMS(merchant: Merchant, advocate_info: AdvocateInfo) {
    const sharing_url = this.getSharingUrl(merchant, advocate_info, "sms")
    let body = <string>get(merchant, "referral_program.pages.templates.advocate_sharing.custom_sms.content")

    if (body) {
      body = encodeURIComponent(this.replaceSharingDynamicValues(body, merchant, advocate_info, sharing_url))
    } else {
      body = sharing_url
    }

    if (this.toolService.isIOSDevice()) {
      window.open(`sms:&body=${body}`)
    } else {
      window.open(`sms:?body=${body}`)
    }
  }

  getSharingEmailInfo(merchant: Merchant, advocate_info: AdvocateInfo, sharing_url?: string) {
    const custom_email = <any>get(merchant, "referral_program.pages.templates.advocate_sharing.custom_email")

    let subject = ""
    let body = ""

    if (custom_email) {
      if (custom_email.subject) {
        subject = this.replaceSharingDynamicValues(custom_email.subject, merchant, advocate_info)
      }

      if (custom_email.content) {
        body = this.replaceSharingDynamicValues(custom_email.content, merchant, advocate_info, sharing_url)
      }
    }

    return {
      subject: subject,
      body: body
    }
  }

  openSharingEmail(merchant: Merchant, advocate_info: AdvocateInfo) {
    const sharing_url = this.getSharingUrl(merchant, advocate_info, "email")
    const { subject, body } = this.getSharingEmailInfo(merchant, advocate_info, sharing_url)
    // window.open doesn't work for mailto on iOS
    window.location.href = `mailto:?body=${encodeURIComponent(body)}&subject=${encodeURIComponent(subject)}`
  }

  replaceDynamicValues(
    string: string,
    merchant: Merchant,
    advocate_info?: AdvocateInfo,
    extra_values?: { [search: string]: string }
  ) {
    const values = {
      "[Company Phone]": merchant.telephone,
      "[Company Email]": merchant.email,
      "[Merchant Name]": merchant.name,
      "[Advocate Reward Name]": merchant.referral_program.advocate_reward
        ? merchant.referral_program.advocate_reward.name
        : "",
      "[Friend Gift Name]": merchant.referral_program.referred_reward
        ? merchant.referral_program.referred_reward.name
        : ""
    }

    if (advocate_info) {
      values["[Advocate First Name]"] = advocate_info.name ? advocate_info.name.given : ""
    }

    if (extra_values) {
      extend(values, extra_values)
    }

    return this.toolService.replaceDynamicValues(string, values)
  }

  loadTemplateDynamicValues(
    template: MerchantReferralCampaignPagesTemplate,
    merchant: Merchant,
    advocate_info?: AdvocateInfo,
    extra_values?: { [search: string]: string }
  ): MerchantReferralCampaignPagesTemplate {
    template = cloneDeep(template)

    if (template.title) {
      template.title = this.replaceDynamicValues(template.title, merchant, advocate_info, extra_values)
    }

    if (template.content) {
      template.content = this.replaceDynamicValues(template.content, merchant, advocate_info, extra_values)
    }

    return template
  }
}



// WEBPACK FOOTER //
// /home/runner/work/onelocal/onelocal/src/my-referral/src/app/referral-magic/referral.service.ts